import axios from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
//import toast from 'react-hot-toast';
import TokenService from './TokenService';
import getResponseError from 'utils/getResponseError';

//axios.defaults.baseURL = 'https://localhost:5001/api';
//axios.defaults.baseURL = 'https://api-grc.austranet.com/api';
axios.defaults.baseURL = import.meta.env.VITE_API_URL;

axios.defaults.params = {
    culture : window.localStorage.getItem('idioma') ?? "es",
};

axios.interceptors.request.use(
    (config) => {
        const token = TokenService.getLocalAccessToken();
        if(token){
            config.headers.Authorization = `Bearer ` + token;
            return config;
        }
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Function that will be called to refresh authorization
const refreshAuthLogic = (failedRequest) =>
    axios.post('/login/refreshToken', {
        grantType: TokenService.getLocalRefreshToken(),
    }).then((tokenRefreshResponse) => {
        TokenService.setLocalTokens(tokenRefreshResponse.data.token, tokenRefreshResponse.data.refreshToken);
        failedRequest.response.config.headers['Authorization'] = 'Bearer ' + tokenRefreshResponse.data.token;
        return Promise.resolve();
    }).catch(() => {
        TokenService.removeTokens();
        window?.location?.reload();
    });

// Instantiate the interceptor
createAuthRefreshInterceptor(axios, refreshAuthLogic);

const getBlob = async (url, onProgress = () => {}) => {
    try {
        const token = TokenService.getLocalAccessToken();
        const response = await fetch(axios.defaults.baseURL + url, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });

        if (!response.ok) {
            onProgress('error', 0);
            return [undefined, 'Error en la descarga', response.status];
        }

        const contentLength = parseInt(response.headers.get('Content-Length') || '0', 10);
        const totalBytes = isNaN(contentLength) ? null : contentLength;

        const reader = response.body.getReader();
        const chunks = [];
        let bytesReceived = 0;

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            chunks.push(value);
            bytesReceived += value.length;

            if (totalBytes) {
                const porcentaje = Math.floor((bytesReceived / totalBytes) * 100);
                onProgress('loading', porcentaje);
            }
        }

        const blob = new Blob(chunks);
        onProgress('downloaded', 100);
        return [{ data: blob }, undefined, response.status];

    } catch (err) {
        onProgress('error', 0);
        return [undefined, err?.message || 'Error desconocido', undefined];
    }
}

/**
 * Sube un archivo por chunks a un endpoint dado.
 *
 * @param {Object} options
 * @param {string} options.url - URL del endpoint (ej: `/AdtPrueba/123/cargarArchivo`)
 * @param {File} options.file - Archivo a subir
 * @param {(estado: 'loading' | 'error' | 'uploaded', progress: number) => void} options.callback
 * @param {number} [options.chunkSize] - Tamaño del chunk (por defecto 8MB)
 * @param {(params: {
 *    fileName: string,
 *    extension: string,
 *    offset: number,
 *    totalSize: number,
 *    chunkIndex: number,
 *    totalChunks: number,
 *    isFirstChunk: boolean,
 *    isLastChunk: boolean
 *  }) => any} [options.buildJsonData] - Permite agregar campos al jsonData
 * @param {(response: any) => string | undefined} [options.getNextFileNameFromResponse]
 *        Función que lee del response el nombre/ruta de archivo para el siguiente chunk
 *
 * @returns {Promise<[any | undefined, any | undefined, number]>}
 */
const uploadFileInChunks = async ({
  url,
  file,
  callback,
  chunkSize = 8 * 1024 * 1024,
  buildJsonData,
  getNextFileNameFromResponse
}) => {
  const totalSize = file.size;
  const totalChunks = Math.ceil(totalSize / chunkSize);
  const extension = file.name.includes('.')
    ? file.name.split('.').pop().toLowerCase()
    : '';

  let fileName = extension
    ? file.name.slice(0, -(extension.length + 1))
    : file.name;

  try {
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const offset = chunkIndex * chunkSize;
      const chunk = file.slice(offset, offset + chunkSize);

      const isFirstChunk = chunkIndex === 0;
      const isLastChunk  = chunkIndex === totalChunks - 1;

      const baseJson = {
        fileName,
        extension,
        offset,
        totalSize,
        isFirstChunk,
        isLastChunk,
        chunkIndex,
        totalChunks
      };

      const jsonData = JSON.stringify(
        typeof buildJsonData === 'function'
          ? buildJsonData(baseJson)
          : baseJson
      );

      const formData = new FormData();
      formData.append('file', chunk);
      formData.append('jsonData', jsonData);

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      };

      const response = await axios.post(url, formData, config);

      if (response.status !== 200) {
        const progressError = Math.floor((chunkIndex / totalChunks) * 100);
        callback('error', progressError);
        return [undefined, 'Error al cargar archivo', response.status];
      }

      const porcentaje = Math.floor(((chunkIndex + 1) / totalChunks) * 100);
      callback('loading', porcentaje);

      // Actualizar fileName según lo que responda el backend (si aplica)
      if (typeof getNextFileNameFromResponse === 'function') {
        const newFileName = getNextFileNameFromResponse(response.data);
        if (newFileName) {
          fileName = newFileName;
        }
      }

      if (isLastChunk) {
        callback('uploaded', 100);
        return [response, undefined, response.status];
      }
    }

    callback('error', 0);
    return [undefined, 'Error desconocido', 500];
  } catch (err) {
    callback('error', 0);
    const error = getResponseError(err);
    return [undefined, error, 500];
  }
};


const requestGenerico = {
    /**
     * Realiza una solicitud GET.
     * @param {string} url - URL del recurso.
     * @param {object} [config] - Configuración opcional de Axios.
     * @returns {Promise<import('axios').AxiosResponse<any>>}
     */
    get: (url, config = undefined) => axios.get(url, config),

    /**
     * Realiza una solicitud POST.
     * @param {string} url - URL del recurso.
     * @param {*} body - Cuerpo de la solicitud.
     * @param {object} [config] - Configuración opcional de Axios.
     * @returns {Promise<import('axios').AxiosResponse<any>>}
     */
    post: (url, body, config = undefined) => axios.post(url, body, config),

    /**
     * Realiza una solicitud PUT.
     * @param {string} url - URL del recurso.
     * @param {*} body - Cuerpo de la solicitud.
     * @param {object} [config] - Configuración opcional de Axios.
     * @returns {Promise<import('axios').AxiosResponse<any>>}
     */
    put: (url, body, config = undefined) => axios.put(url, body, config),

    /**
     * Realiza una solicitud DELETE.
     * @param {string} url - URL del recurso.
     * @param {object} [config] - Configuración opcional de Axios.
     * @returns {Promise<import('axios').AxiosResponse<any>>}
     */
    delete: (url, config = undefined) => axios.delete(url, config),

    /**
     * Obtiene un recurso como Blob con seguimiento de progreso.
     *
     * @param {string} url - URL relativa del recurso.
     * @param {(estado: 'loading' | 'error' | 'downloaded', progress: number) => void} [onProgress]
     *        Callback para progreso de descarga.
     *
     * @returns {Promise<[ { data: Blob } | undefined, string | undefined, number | undefined ]>}
     */
    getBlob: (url, onProgress) => getBlob(url, onProgress),

    /**
     * Sube un archivo en chunks utilizando multipart/form-data.
     *
     * @param {string} url - URL del endpoint para cargar archivo.
     * @param {File} file - Archivo a subir.
     * @param {(estado: 'loading' | 'error' | 'uploaded', progress: number) => void} callback
     *        Callback que notifica el progreso y estado.
     * @param {number} [chunkSize] - Tamaño de cada chunk en bytes (por defecto 8MB).
     * @param {(jsonBase: {
     *     fileName: string,
     *     extension: string,
     *     offset: number,
     *     totalSize: number,
     *     chunkIndex: number,
     *     totalChunks: number,
     *     isFirstChunk: boolean,
     *     isLastChunk: boolean
     *   }) => object} [buildJsonData]
     *        Función opcional para agregar o modificar campos enviados en jsonData por cada chunk.
     *
     * @param {(responseData: any) => string | undefined} [getNextFileNameFromResponse]
     *        Función opcional que extrae del response el nuevo nombre/ruta del archivo
     *        para el siguiente chunk.
     *
     * @returns {Promise<[any | undefined, any | undefined, number]>}
     *          Arreglo con [data, error, status].
     */
    uploadFileInChunks: (url, file, callback, chunkSize, buildJsonData, getNextFileNameFromResponse) => uploadFileInChunks({ url: url, file: file, callback: callback, chunkSize: chunkSize, buildJsonData: buildJsonData, getNextFileNameFromResponse: getNextFileNameFromResponse }),
};

export default requestGenerico;
